<template>
  <!-- Error Message -->
  <PageError v-if="error" :error="error" :errorCode="errorCode" />

  <div v-else-if="canView || user?.is_staff" class="dark:text-white">
    <!-- Stats Cards -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 h-48">
      <div class="glass-card-lite p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-gray-600 dark:text-gray-400 text-sm mb-1">TOTAL DEVICES</p>
            <p class="text-3xl font-bold">{{ devices.length }}</p>
          </div>
          <div class="bg-green-500/20 p-3 rounded-lg">
            <CpuIcon class="text-green-500"/>
          </div>
        </div>
      </div>

      <div class="glass-card-lite p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-gray-600 dark:text-gray-400 text-sm mb-1">ONLINE</p>
            <p class="text-3xl font-bold">{{ onlineCount }}</p>
          </div>
          <div class="bg-blue-500/20 p-3 rounded-lg">
            <Wifi class="text-blue-500"/>
          </div>
        </div>
      </div>

      <div class="glass-card-lite p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-gray-600 dark:text-gray-400 text-sm mb-1">OFFLINE</p>
            <p class="text-3xl font-bold">{{ offlineCount }}</p>
          </div>
          <div class="bg-yellow-500/20 p-3 rounded-lg">
            <WifiOff class="text-yellow-500"/>
          </div>
        </div>
      </div>

      <div class="glass-card-lite p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-gray-600 dark:text-gray-400 text-sm mb-1">ALERTS</p>
            <p class="text-3xl font-bold">{{ alertCount }}</p>
          </div>
          <div class="bg-red-500/20 p-3 rounded-lg">
            <AlertTriangle class="text-red-500"/>
          </div>
        </div>
      </div>
    </div>

    <!-- Header Section -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div class="flex items-center gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-7 h-7 sm:w-8 sm:h-8 bg-white/50 rounded px-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                All Devices
                <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
            </h1>
        </div>

      <div class="flex space-x-3">
        <button
          type="button"
          class="hover:bg-transparent bg-transparent"
          @click="showHowToDevice = true"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="dark:text-gray-50 text-gray-900">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
            <path d="M12 17h.01"/>
          </svg>
        </button>
        <button
          @click="fetchDevices"
          class="btn bg-gray-700 text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          Refresh
        </button>
      </div>
    </div>

    <!-- Devices Table -->
    <GenericMivuLiteTable
      ref="table"
      :columns="columns"
      :items="paginatedDevices"
      itemKey="id"
      :selectable="true"
      @update:selectedItems="updateSelectedDevices"
    >
      <!-- Custom cell templates -->
       <template #name="{value}">
        {{ formatValue("name", value) }}
      </template>
      <template #device_type="{ value }">
        {{ formatValue("device_type", value) }}
      </template>
      <template #status="{ value }">
        <span
          :class="[
            'px-3 py-1 rounded-full text-xs font-medium',
            value ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
          ]"
        >
          {{ value ? 'ONLINE' : 'OFFLINE' }}
        </span>
      </template>
      <template #mac_address="{ value }">
        <span class="text-gray-900 dark:text-cyan-400">{{ value }}</span>
      </template>
      <template #last_seen="{ value }">
        {{ formatValue("last_seen", value) }}
      </template>
      <template #actions="{ item }">
        <div class="flex gap-3 p-4">
            <button 
                class="flex items-center gap-2 btn bg-red-600 border border-red-600 text-white hover:bg-red-700 hover:border-red-700 active:scale-98 transition-all"
                type="button"
                @click.stop="rejectThisDevice(item.mac_address)"
                >
                <Trash2/>
                <span class="text-sm font-medium">Remove</span>
            </button>
        </div>
      </template>
    </GenericMivuLiteTable>
    

    <div class="flex justify-between items-center gap-3 my-6">
        <div class="flex items-center gap-3">
          <HourglassIcon class="text-yellow-500"/>
          <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              Pending Registrations
              <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
          </h1>
        </div>
        
        <div class="flex space-x-3">
          <button
            @click="fetchDevices"
            class="btn bg-gray-700 text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Refresh
          </button>
          <button
            @click="fetchDevices"
            class="btn bg-gray-700 text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Refresh
          </button>
      </div>
    </div>

    <!-- Devices Table -->
    <GenericMivuLiteTable
      ref="table"
      :columns="unacceptedColumns"
      :items="paginatedUnacceptedDevices"
      itemKey="id"
      :selectable="true"
      @update:selectedItems="updateSelectedDevices"
      class="my-6"
    > 
      <!-- Custom cell templates -->
      <template #mac_address="{ value }">
        <span class="text-cyan-400">{{ value }}</span>
      </template>
      <template #created_at="{ value }">
        {{ formatValue("created_at", value) }}
      </template>
      <template #device_type="{ value }">
        {{ formatValue("device_type", value) }}
      </template>
      <template #actions="{ item }">
        <div class="flex gap-3 p-4">
            <button 
                class="flex items-center gap-2 px-5 py-2.5 bg-green-600 border border-slate-600 text-slate-200 rounded-md hover:bg-green-700 hover:border-slate-500 active:scale-98 transition-all"
                type="button"
                @click.stop="acceptThisDevice(item.mac_address)"
                >
                <Check/>
                <span class="text-sm font-medium">Approve</span>
            </button>
            
            <button 
                class="flex items-center gap-2 px-5 py-2.5 bg-red-600 border border-red-600 text-white rounded-md hover:bg-red-700 hover:border-red-700 active:scale-98 transition-all"
                type="button"
                @click.stop="rejectThisDevice(item.mac_address)"
                >
                <Trash2/>
                <span class="text-sm font-medium">Reject</span>
            </button>
        </div>
      </template>
    </GenericMivuLiteTable>

    <DeleteModal
      v-if="deletingItem"
      :deleting-item="deletingItem"
      :componet-objects="componetObjects"
      :is-deleting="isDeleting"
      @delete="confirmDelete"
      @close="deletingItem = false"
    />
  </div>
</template>


<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import SearchBar from '@/components/SearchBar.vue';
import GenericTable from '@/components/GenericTable.vue';
import PageError from '@/components/PageError.vue';
import DeleteModal from '@/components/DeleteModal.vue';
import { getDevices, deleteDevice, getUnacceptedDevices, getAllDevices, acceptDevice } from '@/services/mivulite/devicesService';
import { useStore } from '@/store';
import { AlertTriangle, CpuIcon, Eye, Trash2, Wifi, WifiOff, Hourglass, HourglassIcon, Check } from 'lucide-vue-next';
import GenericMivuLiteTable from '@/components/GenericMivuLiteTable.vue';

const router = useRouter();

// Refs
const showHowToDevice = ref(false);
const devices = ref([]);
const unacceptedDevices = ref([]);
const searchQuery = ref("");
const filteredDevices = ref([]);
const filteredUnacceptedDevices = ref([]);
const currentPage = ref(1);
const itemsPerPage = ref(20);
const selectedDevices = ref([]);
const deletingItem = ref(false);
const bulkAction = ref("");
const canAdd = ref(false);
const canDelete = ref(false);
const canView = ref(false);
const error = ref("");
const errorCode = ref("");
const isDeleting = ref(false);
const table = ref(null);

// Constants
const columns = [
  { key: "name", label: "Name", sortable: true, width: "15%" },
  { key: "mac_address", label: "MAC Address", sortable: true, width: "15%" },
  { key: "status", label: "Status", sortable: true, width: "10%" },
  { key: "device_type", label: "Type", sortable: true, width: "10%" },
  { key: "last_seen", label: "Last Seen", sortable: true, width: "13%" },
  { key: "actions", label: "Actions", sortable: true, width: "13%" },
];


const unacceptedColumns = [
  { key: "mac_address", label: "MAC Address", sortable: true, width: "15%" },
  { key: "created_at", label: "Request Time", sortable: true, width: "15%" },
  { key: "device_type", label: "Type", sortable: true, width: "10%" },
  { key: "actions", label: "Actions", sortable: true, width: "13%" },
];

const errorMessages = {
  400: "Bad Request. The server could not understand the request.",
  401: "Unauthorized access. Please check your authentication token or log in again.",
  403: "You do not have permission to access this resource. Contact a staff member to assign you the permissions to access this page.",
  404: "Page Not Found.",
  500: "Internal Server Error - Something went wrong on the server.",
};

// Computed
const user = computed(() => useStore.getUser());

const isFiltered = computed(() => searchQuery.value !== "");

const onlineCount = computed(() => 
  devices.value.filter(d => d.is_online).length
);

const offlineCount = computed(() => 
  devices.value.filter(d => !d.is_online).length
);

const alertCount = computed(() => 
  devices.value.filter(d => !d.is_accepted || !d.is_whitelisted).length
);

const totalPages = computed(() => {
  return itemsPerPage.value === -1
    ? 1
    : Math.ceil(filteredDevices.value.length / itemsPerPage.value);
});

const paginatedDevices = computed(() => {
  if (itemsPerPage.value === -1) {
    return filteredDevices.value.map(d => ({
      ...d,
      status: d.is_online
    }));
  }
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredDevices.value.slice(start, end).map(d => ({
    ...d,
    status: d.is_online
  }));
});


const paginatedUnacceptedDevices = computed(() => {
  if (itemsPerPage.value === -1) {
    return filteredUnacceptedDevices.value.map(d => ({
      ...d,
      status: d.is_online
    }));
  }
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredUnacceptedDevices.value.slice(start, end).map(d => ({
    ...d,
    status: d.is_online
  }));
});

const componetObjects = computed(() => {
  return selectedDevices.value.map((device) => ({
    values: [
      { name: "Device", value: device.name },
      { name: "MAC Address", value: device.mac_address },
      { name: "Location", value: device.location || "N/A" },
    ],
  }));
});

// Methods
const handleShowHowToDevice = (value) => {
  showHowToDevice.value = value;
};

const checkPermissions = async () => {
  if (user.value) {
    canAdd.value = await useStore.hasPermission("Can add device");
    canDelete.value = await useStore.hasPermission("Can delete device");
    canView.value = await useStore.hasPermission("Can view device");
  }
};

const fetchDevices = async () => {
  try {
    devices.value = await getAllDevices();
    applyFilters();
  } catch (err) {
    if (err.response) {
      error.value = `${errorMessages[err.response.status]}`;
      errorCode.value = `${err.response.status}`;
    } else {
      error.value = `Unknown Error`;
    }
  }
};


const fetchUnacceptedDevices = async () => {
  try {
    unacceptedDevices.value = await getUnacceptedDevices();
    applyUnacceptedFilters();
  } catch (err) {
    if (err.response) {
      error.value = `${errorMessages[err.response.status]}`;
      errorCode.value = `${err.response.status}`;
    } else {
      error.value = `Unknown Error`;
    }
  }
};


const acceptThisDevice = async (mac) => {
  try {
    const response = await acceptDevice(mac)
    await fetchDevices();
    await fetchUnacceptedDevices();
  } catch (error) {
    console.error(error);
  }
};


const rejectThisDevice = async (mac) => {
  try {
    const response = await deleteDevice(mac);
    await fetchDevices();
    await fetchUnacceptedDevices();
  } catch (error) {
    console.error(error);
  }
};


const applyFilters = () => {
  filteredDevices.value = devices.value.filter((device) =>
    device?.name?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    device?.mac_address?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    (device?.location && device?.location.toLowerCase().includes(searchQuery.value.toLowerCase()))
  );
  currentPage.value = 1;
};


const applyUnacceptedFilters = () => {
  filteredUnacceptedDevices.value = unacceptedDevices.value.filter((device) =>
    device?.name?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    device?.mac_address?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    (device?.location && device?.location.toLowerCase().includes(searchQuery.value.toLowerCase()))
  );
  currentPage.value = 1;
};

const updateSelectedDevices = (selectedItems) => {
  selectedDevices.value = selectedItems;
};

const applyBulkAction = async () => {
  if (bulkAction.value === "delete") {
    deletingItem.value = true;
  }
  bulkAction.value = "";
};

const confirmDelete = async () => {
  isDeleting.value = true;
  await deleteSelectedDevices();
  deletingItem.value = false;
  bulkAction.value = "";
};

const deleteSelectedDevices = async () => {
  if (selectedDevices.value.length === 0) {
    alert("No devices selected for deletion");
    return;
  }

  try {
    const deletePromises = selectedDevices.value.map((device) =>
      deleteDevice(device.id)
    );
    await Promise.all(deletePromises);

    devices.value = devices.value.filter(
      (device) => !selectedDevices.value.some((item) => item.id === device.id)
    );
    table.value?.clearSelection();
    applyFilters();
    selectedDevices.value = [];
  } catch (err) {
    console.error("Error during bulk delete:", err);
    alert("An error occurred while deleting devices. Please try again.");
  } finally {
    isDeleting.value = false;
  }
};

const calculateUptime = (lastSeen) => {
  if (!lastSeen) return "N/A";
  const now = new Date();
  const lastSeenDate = new Date(lastSeen);
  const diff = now - lastSeenDate;
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const formatValue = (key, value) => {
  if (key === "last_seen" || key === "created_at") {
    return formatDateTime(value);
  }
  if (key === "device_type" && value === null){
    return 'ESP32'
  }
  return value !== null && value !== undefined ? value : "N/A";
};

const formatDateTime = (dateString) => {
  if (!dateString) return "N/A";
  const options = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  };
  return new Date(dateString).toLocaleString(undefined, options);
};

const changePage = (delta) => {
  currentPage.value += delta;
};

const updatePagination = () => {
  currentPage.value = 1;
      setPersistedPageSize(itemsPerPage.value);
};

// Watchers
watch(searchQuery, () => {
  applyFilters();
});

watch(
  user,
  (newUser) => {
    if (newUser) {
      checkPermissions();
    }
  },
  { immediate: true }
);

// Lifecycle
onMounted(async () => {
  checkPermissions();
  await fetchDevices();
  applyFilters();
  await fetchUnacceptedDevices();
  applyUnacceptedFilters();
});

// Expose methods/refs that might be needed by template ref
defineExpose({
  table,
  handleShowHowToDevice,
  updateSelectedDevices,
  applyBulkAction,
  confirmDelete,
  calculateUptime,
  formatValue,
  formatDateTime,
  changePage,
  updatePagination,
});
</script>